import { BlendMode, toAlphaMode, } from "../../dependencies/conway-geom/index.js";
const ROUGNESS_CONVERSION_FACTOR = 2;
/**
 * Convert a specular exponent (e.g. phong or blinn) to roughness
 *
 * Uses the conversion from - http://simonstechblog.blogspot.com/2011/12/microfacet-brdf.html
 *
 * @param shininess The shinyness exponent/𝛼 to convert to roughness
 * @return {number} The resultant roughness.
 */
export function exponentToRoughness(shininess) {
    return Math.sqrt(ROUGNESS_CONVERSION_FACTOR /
        (shininess + ROUGNESS_CONVERSION_FACTOR));
}
/**
 * Convert a roughness to specular exponent
 *
 * Uses the conversion from - http://simonstechblog.blogspot.com/2011/12/microfacet-brdf.html
 *
 * @param roughness The roughness to convert to shinyness/exponent
 * @return {number} The resultant shinyness.
 */
export function roughnessToExponent(roughness) {
    return (ROUGNESS_CONVERSION_FACTOR /
        (Math.pow(roughness, ROUGNESS_CONVERSION_FACTOR))) -
        ROUGNESS_CONVERSION_FACTOR;
}
/**
 * A materila name cleaned for OBJ syntax
 *
 * @param from The material to get a clean name for
 * @return {string} The cleaned name
 */
export function getMTLCleanName(from) {
    return from.name.replaceAll("#", "_");
}
/**
 * Dump a material to MTL format.
 *
 * @param from The material to dump.
 * @return {string} The MTL formatted string.
 */
export function dumpMTL(from) {
    let result = `newmtl ${getMTLCleanName(from)}\n`;
    const AMBIENT = 0.05;
    result += `Ka ${from.baseColor[0] * AMBIENT} ${from.baseColor[1] * AMBIENT} ${from.baseColor[2] * AMBIENT}\n`;
    if (from.metalness === void 0) {
        result += `Kd ${from.baseColor[0]} ${from.baseColor[1]} ${from.baseColor[2]}\n`;
    }
    else {
        const albedoFactor = 1 - from.metalness;
        result += `Kd ${from.baseColor[0] * albedoFactor} ${from.baseColor[1] * albedoFactor} ${from.baseColor[2] * albedoFactor}\n`;
    }
    result += `d ${from.baseColor[3]}\n`;
    if (from.roughness !== void 0) {
        result += `Ns ${roughnessToExponent(from.roughness)}\n`;
    }
    if (from.ior !== void 0) {
        result += `Ni ${from.ior}\n`;
    }
    if (from.specular !== void 0) {
        result += `Ks ${from.specular[0]} ${from.specular[1]} ${from.specular[2]}\n`;
    }
    else if (from.metalness !== void 0) {
        // This is a physically based interpretation mapped to MTL
        const DEFAULT_IOR = 1.5; // Common diffuse objects like brick, vegetables, bone
        const ior = from.ior ?? DEFAULT_IOR;
        const f0Root = Math.abs((1 - ior) / (1 + ior));
        const f0 = f0Root * f0Root;
        const antiMetal = (1 - from.metalness) * f0;
        const specularR = antiMetal + (from.baseColor[0] * from.metalness);
        const specularG = antiMetal + (from.baseColor[1] * from.metalness);
        const specularB = antiMetal + (from.baseColor[2] * from.metalness);
        result += `Ks ${specularR} ${specularG} ${specularB}\n`;
    }
    if (from.roughness !== void 0 || from.specular !== void 0 || from.metalness !== void 0) {
        result += `illum 2\n`;
    }
    else {
        result += `illum 1\n`;
    }
    return result;
}
export const defaultCanonicalMaterial = {
    name: "Default Conway Material",
    blend: BlendMode.OPAQUE,
    baseColor: [0.7, 0.7, 0.9, 1],
    doubleSided: false,
    legacyColor: [0.7, 0.7, 0.9, 1],
    metalness: 0,
    roughness: 1,
    ior: 1.5,
};
/**
 * Convert a canonical material to a native material
 *
 * @param wasm The wasm object to use for conversion.
 * @param from The material to convert.
 * @return {MaterialObject} The native material object.
 */
export function toNativeMaterial(wasm, from) {
    const native = {
        alphaCutoff: 0,
        alphaMode: toAlphaMode(wasm, from.blend),
        base: {
            x: from.baseColor[0],
            y: from.baseColor[1],
            z: from.baseColor[2],
            w: from.baseColor[3],
        },
        doubleSided: from.doubleSided,
        ior: from.ior ?? 1.5,
        metallic: from.metalness ?? 1,
        roughness: from.roughness ?? 1,
        specular: from.specular !== void 0 ? {
            x: from.specular[0],
            y: from.specular[1],
            z: from.specular[2],
            w: from.specular[3],
        } : {
            x: 0,
            y: 0,
            z: 0,
            w: 1,
        },
    };
    return native;
}
